import React, {useEffect, useState, useCallback} from 'react'
import {showToast} from '../../../auth/components/middleware/showToast'
import {getFile} from '../../../auth/components/middleware/converseBase64'
import Swal from 'sweetalert2'
import {getAge} from '../../../auth/components/middleware/validate'
import vnptVerify from '../../../auth/components/server/Step5'
import * as CryptoJS from 'crypto-js'
import type {
  EkycSdkResult,
  EkycVerifyPayload,
  EkycVerifyRequest,
  VnptSdkConfig,
  VnptSignedPayload,
  VnptSignedResponse,
  VnptToken,
} from '../../types/EkycTypes'

// Add new constants
const ERROR_MESSAGES = {
  NOTHING: 'NOTHING',
  NOMATCH: 'NOMATCH',
  SIZE_EXCEEDED: 'Ảnh vượt quá dung lượng cho phép',
  INVALID_DOC: 'Giấy tờ không hợp lệ',
  UNDERAGE: 'Quý khách không đủ điều kiện mở tài khoản do chưa đủ 18 tuổi',
  LOW_MATCH: 'Quý khách không đủ điều kiện mở tài khoản do chưa đạt tỷ lệ xác thực',
  FAKE_LIVENESS_BOTH_MATCH: 'Giấy tờ không phải giấy tờ thật',
  FAKE_LIVENESS_FRONT_MATCH: 'Giấy tờ mặt trước không phải giấy tờ thật',
  FAKE_LIVENESS_BACK_MATCH: 'Giấy tờ mặt sau không phải giấy tờ thật',
  FAKE_PRINT_BOTH_MATCH: 'Giấy tờ là giấy tờ in màu',
  FAKE_PRINT_FRONT_MATCH: 'Giấy tờ mặt trước là giấy tờ in màu',
  FAKE_PRINT_BACK_MATCH: 'Giấy tờ mặt sau là giấy tờ in màu',
  FAKE_SWAPPING_BOTH_MATCH: 'Ảnh bị dán đè',
  FAKE_SWAPPING_FRONT_MATCH: 'Ảnh mặt trước bị dán đè',
  FAKE_SWAPPING_BACK_MATCH: 'Ảnh mặt sau bị dán đè',
  BLURRED_LIKELIHOOD_BOTH_MATCH: 'Giấy tờ bị mờ/nhòe',
  BLURRED_LIKELIHOOD_FRONT_MATCH: 'Giấy tờ mặt trước bị mờ/nhòe',
  BLURRED_LIKELIHOOD_BACK_MATCH: 'Giấy tờ mặt sau bị mờ/nhòe',
  LIVENESS_MATCH: 'Không phải người thật',
} as const

const VALID_TYPE_IDS = [1, 5, 6]
const CARD_TYPE = 'CĂN CƯỚC CÔNG DÂN'
const IDENTITY_TYPE = 'CĂN CƯỚC'
const VALID_CARD_TYPES = [CARD_TYPE, IDENTITY_TYPE]
const MAX_FILE_SIZE = 4000000
const MIN_AGE = 18
const MIN_MATCH_PROBABILITY = 80
const MIN_FAKE_LIVENESS = 0.55
const MIN_FAKE_PRINT = 0.7
const MIN_SWAPPING = 0.6
const MIN_LIVENESS = 0.5
const BLURRED = 'likely'

const genRanHex = (size: number) =>
  [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')

const getBase64ImageSize = (base64Image?: string) => {
  if (!base64Image) return 0

  try {
    return getFile(base64Image, 'ekyc').size
  } catch {
    return 0
  }
}

const pickSignedPayload = (part?: VnptSignedResponse): VnptSignedPayload => ({
  dataSign: part?.dataSign || '',
  dataBase64: part?.dataBase64 || '',
  logID: part?.logID,
})

const buildVerifyPayload = (result: EkycSdkResult): EkycVerifyPayload => ({
  ocr: pickSignedPayload(result.ocr),
  liveness_card_front: pickSignedPayload(result.liveness_card_front),
  liveness_card_back: pickSignedPayload(result.liveness_card_back),
  liveness_face: pickSignedPayload(result.liveness_face),
  compare: pickSignedPayload(result.compare),
  masked: result.masked ? pickSignedPayload(result.masked) : undefined,
  base64_doc_img: result.base64_doc_img,
  base64_doc_img_source: result.base64_doc_img_source,
})

/**
 * Encrypts data using AES symmetric encryption.
 * @param data The plaintext data to encrypt.
 * @param key The symmetric key, must be 32 characters for AES-256.
 */
function aesEncrypt(data: string, key?: string) {
  if (!key) {
    throw new Error('Missing VNPT verify key.')
  }

  if (key.length !== 32) {
    throw new Error('Key must be 32 characters long for AES-256 encryption.')
  }
  const iv = CryptoJS.enc.Hex.parse(genRanHex(32))
  const encryptedData = CryptoJS.AES.encrypt(data, CryptoJS.enc.Utf8.parse(key), {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })

  return {
    encryptedData: encryptedData.toString(),
    iv: iv.toString(CryptoJS.enc.Base64url),
  }
}

interface Step7ScanFaceProps {
  onGetData: (data?: EkycSdkResult) => void
  setActiveStep: (active: boolean) => void
  getTokenVNPT?: VnptToken
  stepper: {
    current: {
      goNext: () => void
    }
  }
  mobile?: string
}

const Step7ScanFace = ({
  onGetData,
  setActiveStep,
  getTokenVNPT,
  stepper,
  mobile,
}: Step7ScanFaceProps) => {
  const [resEKYC, setResEKYC] = useState<EkycSdkResult>()
  const [reloadEkycVNPT, setReloadEkycVNPT] = useState(0)

  useEffect(() => {
    onGetData(resEKYC)
  }, [onGetData, resEKYC])

  const hanleReloadEkyc = useCallback(() => {
    setReloadEkycVNPT((prev) => prev + 1)
    setActiveStep(true)
  }, [setActiveStep])

  const CALL_BACK_END_FLOW = async (result: EkycSdkResult) => {
    setResEKYC(result)
  }

  useEffect(() => {
    if (getTokenVNPT) {
      const mainNode = document.getElementById('nhsv-ekyc-main')
      if (mainNode) {
        mainNode.innerHTML = ''

        const div2 = document.createElement('div')
        div2.id = 'ekyc_sdk_intergrated'
        mainNode.appendChild(div2)
      }
      const dataConfig: VnptSdkConfig = {
        BACKEND_URL: 'https://api.idg.vnpt.vn',
        TOKEN_KEY: getTokenVNPT?.tokenKey,
        TOKEN_ID: getTokenVNPT?.tokenId,
        ACCESS_TOKEN: getTokenVNPT?.authorization,
        HAS_RESULT_SCREEN: false,
        FLOW_TAKEN: 'BOTH',
        LIST_TYPE_DOCUMENT: [-1],
        CHECK_LIVENESS_CARD: true,
        CHECK_LIVENESS_FACE: true,
        CHECK_MASKED_FACE: true,
        COMPARE_FACE: true,
        DEFAULT_LANGUAGE: 'vi',
        SHOW_STEP: false,
        URL_WEB_OVAL: process.env.PUBLIC_URL + '/lib/web-oval.json',
        URL_MOBILE_OVAL: process.env.PUBLIC_URL + '/lib/mobile-oval.json',
        URL_ENGLISH_VIDEO_TUTORIAL: process.env.PUBLIC_URL + '/lib/english-tutorial.mp4',
        URL_VIETNAMESE_VIDEO_TUTORIAL: process.env.PUBLIC_URL + '/lib/vietnamese-tutorial.mp4',
        COMPRESS_IMAGE: true,
        // USE_METHOD: 'PHOTO',
        // CUTOM_THEME: {
        //   PRIMARY_COLOR: '#1878ff',
        //   TEXT_COLOR_DEFAULT: '#000000',
        //   BACKGROUND_COLOR: '#faad14',
        // },
        CALL_BACK_END_FLOW: CALL_BACK_END_FLOW,
      }
      if (window.SDK) {
        window.SDK.launch(dataConfig)
      }
    }
  }, [getTokenVNPT, reloadEkycVNPT])

  const showErrorDialog = useCallback(
    (title?: string, index?: number) => {
      Swal.fire({
        title: title || 'Dữ liệu eKYC không hợp lệ',
        icon: 'error',
        allowOutsideClick: false,
        html: `${
          index ? `(${index})` : ''
        }Vui lòng liên hệ NHSV theo số <b>19001055</b> để biết thêm chi tiết.`,
        confirmButtonText: 'Thực hiện lại',
      }).then((result) => {
        if (result.isConfirmed) {
          hanleReloadEkyc()
        }
      })
      setActiveStep(true)
    },
    [hanleReloadEkyc, setActiveStep]
  )
  const validateEkycResult = useCallback(() => {
    if (!resEKYC) return

    const frontDocImageSize = getBase64ImageSize(resEKYC?.base64_doc_img?.img_front)
    const backDocImageSize = getBase64ImageSize(resEKYC?.base64_doc_img?.img_back)

    // Check for OCR errors first
    if (resEKYC?.ocr?.errors) {
      showErrorDialog(resEKYC.ocr.errors[0], 6)
      return
    }

    if (
      (resEKYC?.liveness_card_front?.object?.fake_liveness_prob ?? 0) > MIN_FAKE_LIVENESS &&
      (resEKYC?.liveness_card_back?.object?.fake_liveness_prob ?? 0) > MIN_FAKE_LIVENESS
    ) {
      showErrorDialog(ERROR_MESSAGES.FAKE_LIVENESS_BOTH_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_front?.object?.fake_liveness_prob ?? 0) > MIN_FAKE_LIVENESS) {
      showErrorDialog(ERROR_MESSAGES.FAKE_LIVENESS_FRONT_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_back?.object?.fake_liveness_prob ?? 0) > MIN_FAKE_LIVENESS) {
      showErrorDialog(ERROR_MESSAGES.FAKE_LIVENESS_BACK_MATCH)
      return
    }
    // fake_liveness_prob end
    // fake_print_photo_prob start
    if (
      (resEKYC?.liveness_card_front?.object?.fake_print_photo_prob ?? 0) > MIN_FAKE_PRINT &&
      (resEKYC?.liveness_card_back?.object?.fake_print_photo_prob ?? 0) > MIN_FAKE_PRINT
    ) {
      showErrorDialog(ERROR_MESSAGES.FAKE_PRINT_BOTH_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_front?.object?.fake_print_photo_prob ?? 0) > MIN_FAKE_PRINT) {
      showErrorDialog(ERROR_MESSAGES.FAKE_PRINT_FRONT_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_back?.object?.fake_print_photo_prob ?? 0) > MIN_FAKE_PRINT) {
      showErrorDialog(ERROR_MESSAGES.FAKE_PRINT_BACK_MATCH)
      return
    }
    // fake_print_photo_prob end
    // face_swapping_prob start
    if (
      (resEKYC?.liveness_card_front?.object?.face_swapping_prob ?? 0) > MIN_SWAPPING &&
      (resEKYC?.liveness_card_back?.object?.face_swapping_prob ?? 0) > MIN_SWAPPING
    ) {
      showErrorDialog(ERROR_MESSAGES.FAKE_SWAPPING_BOTH_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_front?.object?.face_swapping_prob ?? 0) > MIN_SWAPPING) {
      showErrorDialog(ERROR_MESSAGES.FAKE_SWAPPING_FRONT_MATCH)
      return
    }
    if ((resEKYC?.liveness_card_back?.object?.face_swapping_prob ?? 0) > MIN_SWAPPING) {
      showErrorDialog(ERROR_MESSAGES.FAKE_SWAPPING_BACK_MATCH)
      return
    }
    // face_swapping_prob end
    // liveness_prob start
    if ((resEKYC?.liveness_face?.object?.liveness_prob ?? 0) >= MIN_LIVENESS) {
      showErrorDialog(ERROR_MESSAGES.LIVENESS_MATCH)
      return
    }
    // liveness_prob end

    // blurred_likelihood both start
    if (
      resEKYC?.ocr?.object?.quality_front?.final_result?.blurred_likelihood === BLURRED &&
      resEKYC?.ocr?.object?.quality_back?.final_result?.blurred_likelihood === BLURRED
    ) {
      showErrorDialog(ERROR_MESSAGES.BLURRED_LIKELIHOOD_BOTH_MATCH)
      return
    }
    // blurred_likelihood both end

    // blurred_likelihood front start
    if (resEKYC?.ocr?.object?.quality_front?.final_result?.blurred_likelihood === BLURRED) {
      showErrorDialog(ERROR_MESSAGES.BLURRED_LIKELIHOOD_FRONT_MATCH)
      return
    }
    // blurred_likelihood front end

    // blurred_likelihood back start
    if (resEKYC?.ocr?.object?.quality_back?.final_result?.blurred_likelihood === BLURRED) {
      showErrorDialog(ERROR_MESSAGES.BLURRED_LIKELIHOOD_BACK_MATCH)
      return
    }
    // blurred_likelihood back end

    // Check file sizes
    if (frontDocImageSize > MAX_FILE_SIZE || backDocImageSize > MAX_FILE_SIZE) {
      showErrorDialog(ERROR_MESSAGES.SIZE_EXCEEDED, 7)
      return
    }

    // Check comparison results
    if (resEKYC?.compare?.object?.msg === ERROR_MESSAGES.NOTHING) {
      showErrorDialog(resEKYC.compare.object.result)
      return
    }

    if (resEKYC?.compare?.object?.msg === ERROR_MESSAGES.NOMATCH) {
      showErrorDialog(resEKYC.compare.object.result)
      return
    }

    if ((resEKYC?.compare?.object?.prob ?? MIN_MATCH_PROBABILITY) < MIN_MATCH_PROBABILITY) {
      showErrorDialog(ERROR_MESSAGES.LOW_MATCH, 3)
      return
    }

    // Check OCR warnings
    if (resEKYC?.ocr?.object?.warning) {
      showErrorDialog(resEKYC.ocr.object.warning_msg?.[0], 1)
      return
    }

    if (resEKYC?.ocr?.object?.general_warning?.[0]) {
      showErrorDialog(resEKYC.ocr.object.general_warning?.[0], 2)
      return
    }

    // Check document type and age
    if (
      !VALID_TYPE_IDS.includes(resEKYC?.ocr?.object?.type_id as number) ||
      !VALID_CARD_TYPES.includes(resEKYC?.ocr?.object?.card_type as string)
    ) {
      showErrorDialog(ERROR_MESSAGES.INVALID_DOC, 4)
      return
    }

    if (getAge(resEKYC?.ocr?.object?.birth_day) < MIN_AGE) {
      showErrorDialog(ERROR_MESSAGES.UNDERAGE, 5)
      return
    }

    const {encryptedData, iv} = aesEncrypt(
      JSON.stringify(buildVerifyPayload(resEKYC)),
      process.env.REACT_APP_VNPT_VERIFY_KEY_PATH
    )
    const requestData: EkycVerifyRequest = {
      idno: resEKYC?.ocr?.object?.id,
      mobile: mobile,
      data: `${iv}.${encryptedData}`,
    }
    vnptVerify(requestData)
      .then((res: any) => {
        if (res?.success === false) {
          showErrorDialog(res?.error_desc)
        } else {
          setActiveStep(false)
          stepper.current.goNext()
        }
      })
      .catch(() => {
        showToast('Liên hệ với admin', 2000, 'error')
      })
  }, [mobile, resEKYC, showErrorDialog, setActiveStep, stepper])

  // Single useEffect for all validations
  useEffect(() => {
    validateEkycResult()
  }, [resEKYC, validateEkycResult])

  return (
    <div className='w-100'>
      <div id='nhsv-ekyc-main'>
        <div id='ekyc_sdk_intergrated'></div>
      </div>
    </div>
  )
}

export {Step7ScanFace}
